import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import {
  canonicalPickerApi,
  isScopedSource,
  variantsSourceOf,
} from 'api/canonicalPicker'
import {
  CanonicalLevel,
  PickerSliceState,
  picksOf,
  scopeOf,
  selectActiveSelection,
  selectActiveSource,
  selectGroupConfig,
  sourceChoiceOf,
} from 'slices/aiSegmentBuilder/picker'

import selectionVersionsOf, {
  groupsByIdOf,
  variantProductIdsOf,
  variantsByProductOf,
} from './selectionVersions'

type RootState = { picker: PickerSliceState }

/**
 * The UIDs behind a level's picks, read only while the modal that lists
 * them is open. Variants are always read with the merchant's own token, so
 * they only hold the locations the merchant can see.
 */
const useSelectionVersions = (
  pickerId: number,
  level: CanonicalLevel,
  isOpen: boolean
) => {
  const config = useSelector(
    (state: RootState) => selectGroupConfig(state, pickerId)[level]
  )
  const activeSource = useSelector((state: RootState) =>
    selectActiveSource(state, pickerId, level)
  )
  const selection = useSelector((state: RootState) =>
    selectActiveSelection(state, pickerId, level)
  )
  // Level 2's smart source is filtered to level 1's picks, so its lookups are too
  const scope = useSelector(
    (state: RootState) =>
      level === 'level2'
        ? scopeOf(selectActiveSelection(state, pickerId, 'level1'))
        : null,
    shallowEqual
  )
  const choice = useMemo(() => sourceChoiceOf(config), [config])
  const variantsSource = useMemo(
    () => choice && variantsSourceOf(choice.smart),
    [choice]
  )
  const picks = useMemo(() => picksOf(selection), [selection])
  const isSpecific = activeSource === choice?.specific.name
  // Every pick finds its store UIDs, prices and locations among its products' variants
  const productIds = useMemo(
    () => Array.from(new Set(picks.flatMap(variantProductIdsOf))),
    [picks]
  )
  const uidGroups = useMemo(
    () =>
      Array.from(
        new Set(
          picks.flatMap(pick =>
            pick.kind === 'uidGroup' ? [pick.uidGroup] : []
          )
        )
      ),
    [picks]
  )

  const isAvailable = Boolean(variantsSource) && picks.length > 0
  const needsVariants = isOpen && isAvailable && productIds.length > 0
  const variantsQuery = canonicalPickerApi.useGetPickerRowsByKeyQuery(
    variantsSource && needsVariants
      ? {
          source: variantsSource,
          kind: 'product',
          keys: productIds,
          // Only a scoped source reads it, and every other change of it would refetch the same rows
          scope: isScopedSource(variantsSource) ? scope : null,
        }
      : skipToken
  )
  // Picked groups are described by their group rows, read from the specific source
  const needsGroups = isOpen && isAvailable && uidGroups.length > 0
  const groupsQuery = canonicalPickerApi.useGetPickerRowsByKeyQuery(
    choice && needsGroups
      ? {
          source: choice.specific,
          kind: 'uidGroup',
          keys: uidGroups,
          scope: null,
        }
      : skipToken
  )

  const versions = useMemo(() => {
    if (!isOpen) return null
    const variants = needsVariants
      ? variantsQuery.currentData &&
        variantsByProductOf(variantsQuery.currentData.rows)
      : new Map()
    const groups = needsGroups
      ? groupsQuery.currentData && groupsByIdOf(groupsQuery.currentData.rows)
      : new Map()
    return variants && groups
      ? selectionVersionsOf(picks, isSpecific, variants, groups)
      : null
  }, [
    groupsQuery.currentData,
    isOpen,
    isSpecific,
    needsGroups,
    needsVariants,
    picks,
    variantsQuery.currentData,
  ])

  const { isError: variantsFailed, refetch: refetchVariants } = variantsQuery
  const { isError: groupsFailed, refetch: refetchGroups } = groupsQuery
  const retry = useCallback(() => {
    if (variantsFailed) refetchVariants()
    if (groupsFailed) refetchGroups()
  }, [groupsFailed, refetchGroups, refetchVariants, variantsFailed])

  return {
    isAvailable,
    isSpecific,
    versions,
    isError: variantsFailed || groupsFailed,
    retry,
  }
}

export default useSelectionVersions
